import { useMemo } from 'react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  LabelList,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { Alert, Box, Card, Icon, Text, TextButton } from '@zillow/constellation-10';
import { IconSearchFilled } from '@zillow/constellation-icons';

import { RentalManagerFreemiumAnalyticsFilterType } from '@zg-rentals/ts-rental-manager-api-client';
import {
  useRentalManagerUserFreemiumAnalyticsWebApiHandler_overview,
  useRentalManagerUserFreemiumAnalyticsWebApiHandler_prices,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';
import { getCookieAsBoolean } from '@zg-rentals/zillow-rental-manager/client';

import { useScreenSize } from '../../../../hooks/useScreenSize';
import stringHelper from '../../../../utils/stringHelper';

import { ZRM_USE_FREEMIUM_TEST_DATA } from '../../../../constants/FeatureFlagConstants';
import { COLORS } from '../../constants';

import Filters from './components/Filters';

import {
  circleStyles,
  containerGridRecipe,
  flexEndLegendStyles,
  gridRecipe,
  legendStyles,
  middleLegendSpacerStyles,
} from '../../styles';

import EmptyState from '../../../EmptyState';
import SpinnerContainer from '../../../SpinnerContainer';
import { usePremiumAnalyticsContext } from '../../PremiumAnalyticsContext';
import ActiveCompsTooltip from '../ActiveCompsTooltip';
import CustomLabel from '../CustomLabel';

import type { TooltipProps } from 'recharts';
import type { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';

type RentPricingChartProps = {
  listingAlias: string;
  isInModal?: boolean;
};

const RentPricingChart = ({ listingAlias, isInModal = false }: RentPricingChartProps) => {
  const { filters } = usePremiumAnalyticsContext();
  const { beds, filterType, propertyType } = filters;
  const { isScreenDesktop } = useScreenSize();

  const useTestData = getCookieAsBoolean(ZRM_USE_FREEMIUM_TEST_DATA);

  const {
    data: pricingData,
    isLoading,
    error,
    refetch: refetchGetListingPrices,
  } = useRentalManagerUserFreemiumAnalyticsWebApiHandler_prices(
    { listingAlias, bedrooms: beds, filterType, propertyType, useTestData },
    {
      enabled: !!listingAlias,
      placeholderData: (previousData) => previousData,
    },
  );

  const { data: overviewData } = useRentalManagerUserFreemiumAnalyticsWebApiHandler_overview(
    {
      listingAlias,
      filterType: RentalManagerFreemiumAnalyticsFilterType.CITY_STATE,
      beds: undefined,
      propertyType: undefined,
    },
    { enabled: !!listingAlias },
  );

  // Transform prices data to include labelListDataKey for CustomLabel component
  const transformedPrices = useMemo(() => {
    if (!pricingData?.prices || !pricingData?.maxCount) return [];

    return pricingData.prices.map((priceData) => ({
      ...priceData,
      labelListDataKey: `isProperty:${priceData.isProperty},isMarketMedian:${priceData.isMarketMedian},count:${priceData.count},maxCount:${pricingData.maxCount}`,
    }));
  }, [pricingData?.prices, pricingData?.maxCount]);

  const propertyPrice: number | null = useMemo(
    () =>
      typeof pricingData?.property?.price !== 'undefined' ? Number(pricingData?.property?.price.substring(1)) : null,
    [pricingData?.property?.price],
  );

  if (!overviewData || !pricingData) {
    return null;
  }

  if (isLoading) {
    return (
      <Box
        css={{
          ...containerGridRecipe.raw({ includeWhiteBackground: true }),
          minHeight: { base: '680px', xl: '436px' },
          padding: '1200',
          gridTemplateColumns: 'repeat(1, 1fr)',
        }}
      >
        <Box css={{ display: 'flex', justifyContent: 'center', alignContent: 'center' }}>
          <SpinnerContainer height="auto" />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert tone="warning" actionButton={<TextButton onClick={() => refetchGetListingPrices()}>Retry now</TextButton>}>
        We are having issues loading your market pricing data
      </Alert>
    );
  }

  const renderTooltip = ({ payload }: TooltipProps<ValueType, NameType>) => {
    const { price, count } = payload?.[0]?.payload || {};
    return payload && price ? (
      <Card outlined={false} elevated={true} tone="neutral">
        <Text textStyle="body-xs-bold" asChild>
          <p>{price.includes('-') || price.includes('+') ? price : `${price} - $${Number(price.substring(1)) + 99}`}</p>
        </Text>
        <Text textStyle="body-xs">{`${stringHelper.pluralize(count, 'listing', 'listings')}`}</Text>
      </Card>
    ) : null;
  };

  const renderLegend = () => {
    if (!pricingData?.property?.price || !pricingData?.marketMedian?.price) {
      return null;
    }

    const shouldDisplayPropertyPriceFirst =
      propertyPrice !== null && pricingData?.property?.price < pricingData?.marketMedian?.price;
    const propertyKey = (
      <>
        <Box css={{ ...circleStyles, backgroundColor: 'bg.accent.blue.hero' }} />
        <Text textStyle="body-sm" css={{ marginInline: '200', color: 'text.subtle' }}>
          Your listing
        </Text>
        <Text textStyle="body-sm-bold" asChild>
          <span>{pricingData?.property?.price}</span>
        </Text>
      </>
    );

    const marketMedianKey = (
      <>
        <Box css={{ ...circleStyles, backgroundColor: 'icon.onImpact.feedback.warning.hero' }} />
        <Box css={{ marginInline: '200' }}>
          <Text textStyle="body-sm" css={{ color: 'text.subtle' }}>
            <ActiveCompsTooltip triggerText="Median of comparable listings" />
          </Text>
        </Box>
        <Text textStyle="body-sm-bold" asChild>
          <span>{pricingData?.marketMedian?.price}</span>
        </Text>
      </>
    );
    return (
      <Box
        css={{
          ...legendStyles,
          paddingBottom: '100',
        }}
      >
        <Box css={{ display: 'flex', alignItems: 'center' }}>
          {shouldDisplayPropertyPriceFirst ? propertyKey : marketMedianKey}
        </Box>
        <Box css={middleLegendSpacerStyles}>
          {propertyPrice !== null && (
            <Box css={{ display: 'flex', alignItems: 'center' }}>
              {shouldDisplayPropertyPriceFirst ? marketMedianKey : propertyKey}
            </Box>
          )}
        </Box>
        <Box css={flexEndLegendStyles}>
          <TextButton asChild tone="subtle" textStyle="body-sm">
            <a target="_blank" rel="noopener noreferrer" href={overviewData.compsUrl}>
              Source: active comparable listings
            </a>
          </TextButton>
        </Box>
      </Box>
    );
  };

  return (
    <Box
      css={{
        ...gridRecipe.raw({ noOverflow: false }),
        gridColumnEnd: { base: 'span 4', xl: 'span 2' },
      }}
    >
      {pricingData?.hasActiveComps ? (
        <>
          <Box
            css={{
              display: 'flex',
              justifyContent: isInModal ? 'space-between' : 'flex-start',
              flexDirection: { base: 'column', xl: 'row' },
              alignItems: { base: 'flex-start', xl: 'center' },
            }}
          >
            <Text
              css={{
                textStyle: { base: 'body-sm-bold', xl: 'body-sm' },
                marginBottom: { base: '400', xl: '600' },
              }}
            >
              Number of <ActiveCompsTooltip triggerText="active comparable listings" /> by monthly rent
            </Text>
            {isInModal && <Filters listingAlias={listingAlias} />}
          </Box>
          <ResponsiveContainer width="99%" height={300}>
            <ComposedChart margin={{ left: -20 }} height={400} data={transformedPrices}>
              <XAxis
                dataKey="price"
                padding={{ left: 10 }}
                tickLine={false}
                style={{ fill: COLORS.TEXT_MEDIUM, fontSize: '12px' }}
              />
              <YAxis
                allowDecimals={false}
                tickCount={5}
                type="number"
                tickLine={false}
                stroke="#FFF"
                style={{ fill: COLORS.TEXT_MEDIUM, fontSize: '14px' }}
              />
              <Tooltip content={renderTooltip} />
              <Legend content={() => renderLegend()} />
              <CartesianGrid horizontal={true} vertical={false} stroke={COLORS.GRAY200} />
              <Bar
                animationDuration={0}
                isAnimationActive={false}
                dataKey="count"
                name="Your property"
                barSize={isScreenDesktop ? 20 : 12}
                fill={COLORS.LIGHTBLUE}
              >
                <LabelList
                  dataKey="labelListDataKey"
                  color={COLORS.BLUE500}
                  position="insideTop"
                  content={<CustomLabel />}
                />
              </Bar>
            </ComposedChart>
          </ResponsiveContainer>
        </>
      ) : (
        <Box
          css={{
            // oxlint-disable-next-line constellation/no-raw-spacing-values
            height: '300px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
          }}
        >
          <EmptyState
            icon={<Icon render={<IconSearchFilled />} size="md" css={{ color: 'icon.subtle' }} />}
            heading="Not enough information available"
            headingLevel={3}
            useCard={false}
            align="center"
          />
        </Box>
      )}
    </Box>
  );
};

export default RentPricingChart;
